import { z } from 'zod'

import { appointmentSchema } from '../../entities/appointment'
import { blockedSlotSchema } from '../../entities/blocked-slot'
import { clinicIdSchema } from '../../entities/clinic'
import { communicationSchema } from '../../entities/communication'
import { consultationSchema, consultationStatusSchema } from '../../entities/consultation'
import { memberSchema } from '../../entities/member'
import { noteSchema } from '../../entities/note'
import { orderSchema } from '../../entities/order'
import { orderAllocationSchema } from '../../entities/order-allocation'
import { patientSchema } from '../../entities/patient'
import {
  catalogProductSchema,
  productDerivedFieldsSchema,
  productSchema,
} from '../../entities/product'
import { scheduleOverrideSchema } from '../../entities/schedule-override'
import { scheduleTemplateSchema } from '../../entities/schedule-template'
import { staffMemberSchema } from '../../entities/staff'
import { stockSchema } from '../../entities/stock'
import {
  treatmentDerivedFieldsSchema,
  treatmentPrescriptionContextSchema,
  treatmentSchema,
} from '../../entities/treatment'
import { treatmentReminderSchema } from '../../entities/treatment-reminder'
import { vitalsSchema } from '../../entities/vitals'
import { isoDateTime } from '../../lib/date'
import { fileResponseSchema } from './file'

const clinicSummarySchema = z.object({
  id: clinicIdSchema,
  name: z.string(),
  address: z.string(),
  timezone: z.string(),
})

export const patientWithOwnersSchema = patientSchema.extend({
  owner: memberSchema,
  /** Not yet implemented in the DB model — placeholder for future co-ownership support. */
  coOwners: z.array(memberSchema),
})

export type PatientWithOwners = z.output<typeof patientWithOwnersSchema>

/**
 * A recorded outbound message plus the pet it was about. The same shape serves the
 * member and patient timelines — the patient timeline simply already knows the pet.
 */
export const communicationWithPatientSchema = communicationSchema.extend({
  patient: patientSchema.nullable(),
  externalUrl: z.string().nullable(),
})

export type CommunicationWithPatient = z.output<typeof communicationWithPatientSchema>

export const memberWithPetsSchema = memberSchema.extend({ pets: z.array(patientSchema) })

export type MemberWithPets = z.output<typeof memberWithPetsSchema>

export const memberWithPetSummariesSchema = memberSchema
  .pick({
    id: true,
    firstName: true,
    lastName: true,
    email: true,
    phone: true,
    address1: true,
    address2: true,
    city: true,
    postalCode: true,
    country: true,
    archivedAt: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({ pets: z.array(z.object({ id: z.uuid(), name: z.string() })) })

export type MemberWithPetSummaries = z.output<typeof memberWithPetSummariesSchema>

export const productWithStockSchema = productSchema.extend({
  ...productDerivedFieldsSchema.shape,
  stock: z.array(stockSchema),
})

export type ProductWithStock = z.output<typeof productWithStockSchema>

/** A product as the products routes return it; `stock` is present when `include=stock` is passed. */
export const productResponseSchema = productWithStockSchema.partial({ stock: true })

export const orderAllocationWithStockSchema = orderAllocationSchema.extend({ stock: stockSchema })

export type OrderAllocationWithStock = z.output<typeof orderAllocationWithStockSchema>

export const orderWithAllocationsSchema = orderSchema.extend({
  /** True when this order is a repeat fill — i.e. not the first order on its treatment. */
  isRefill: z.boolean(),
  allocations: z.array(orderAllocationWithStockSchema),
  preparedByStaff: staffMemberSchema.nullable(),
  /** Who handed the order over or administered it. Null until the order is dispensed. */
  dispensedByStaff: staffMemberSchema.nullable(),
})

export type OrderWithAllocations = z.output<typeof orderWithAllocationsSchema>

/** The full enriched treatment shape returned by all `/treatments` routes. */
export const treatmentWithRelationsSchema = treatmentSchema.extend({
  ...treatmentDerivedFieldsSchema.shape,
  product: productSchema.extend(productDerivedFieldsSchema.shape).nullable(),
  catalogProduct: catalogProductSchema.nullable(),
  orders: z.array(orderWithAllocationsSchema),
  staff: staffMemberSchema,
  reminders: z.array(treatmentReminderSchema),
})

export type TreatmentWithRelations = z.output<typeof treatmentWithRelationsSchema>

/** An order with everything the dispensing queues and checkout show alongside it. */
export const orderWithRelationsSchema = orderSchema.extend({
  /** True when this order is a repeat fill — i.e. not the first order on its treatment. */
  isRefill: z.boolean(),
  patient: patientSchema.extend({ owner: memberSchema }),
  product: productSchema,
  treatment: treatmentSchema
    .extend({
      product: productSchema.nullable(),
      orders: z.array(orderSchema.pick({ id: true, createdAt: true })),
      staff: staffMemberSchema,
    })
    .nullable(),
  allocations: z.array(orderAllocationWithStockSchema),
  clinic: clinicSummarySchema,
  /** Present when the order is linked to a consultation. */
  consultation: consultationSchema
    .extend({ staff: staffMemberSchema.nullable(), appointment: appointmentSchema.nullable() })
    .nullable(),
})

export type OrderWithRelations = z.output<typeof orderWithRelationsSchema>

/** Appointment with nested patient (+ owner), clinic, staff, and consultation. */
export const appointmentWithRelationsSchema = appointmentSchema.extend({
  /** Display name from APPOINTMENT_TYPES or the title-cased type ID. */
  appointmentTypeName: z.string(),
  patient: patientWithOwnersSchema,
  clinic: z.object({ id: clinicIdSchema, name: z.string(), timezone: z.string() }).nullable(),
  staff: staffMemberSchema.pick({ id: true, firstName: true, lastName: true }),
  consultation: z.object({ id: z.string(), finalisedAt: isoDateTime.nullable() }).nullable(),
})

export type AppointmentWithRelations = z.output<typeof appointmentWithRelationsSchema>

/** Note with the creating + authoring staff members and any attached files resolved. */
export const noteWithStaffSchema = noteSchema.and(
  z.object({
    createdByStaff: staffMemberSchema,
    writtenByStaff: staffMemberSchema,
    files: z.array(fileResponseSchema),
  })
)

export type NoteWithStaff = z.output<typeof noteWithStaffSchema>

/** A consultation with all its clinical records — for the workspace, timeline and lists. */
export const consultationWithRelationsSchema = consultationSchema.extend({
  status: consultationStatusSchema,
  intercomUrl: z.string().nullable(),
  patient: patientSchema.extend({ owner: memberSchema }),
  clinic: clinicSummarySchema.nullable(),
  staff: staffMemberSchema.nullable(),
  finalisedByStaff: staffMemberSchema.nullable(),
  appointment: appointmentSchema.nullable(),
  vitals: vitalsSchema.nullable(),
  notes: z.array(noteWithStaffSchema),
  treatments: z.array(
    treatmentWithRelationsSchema.extend(treatmentPrescriptionContextSchema.shape)
  ),
})

export type ConsultationWithRelations = z.output<typeof consultationWithRelationsSchema>

/** A schedule override with its joined staff and template (for display). */
export const scheduleOverrideWithRelationsSchema = scheduleOverrideSchema.extend({
  staff: staffMemberSchema.pick({ id: true, firstName: true, lastName: true, email: true }),
  template: scheduleTemplateSchema.pick({ id: true, name: true }),
})

export type ScheduleOverrideWithRelations = z.output<typeof scheduleOverrideWithRelationsSchema>

export const blockedSlotWithStaffSchema = blockedSlotSchema.extend({
  staff: staffMemberSchema.pick({ id: true, firstName: true, lastName: true, email: true }),
  createdByStaff: staffMemberSchema
    .pick({ id: true, firstName: true, lastName: true, email: true })
    .nullable(),
})

export type BlockedSlotWithStaff = z.output<typeof blockedSlotWithStaffSchema>
